import { Bot, CheckCircle2, ExternalLink, RefreshCw, Send, Unplug } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  disconnectBot,
  loadBot,
  sendBotTestMessage,
  setBotEnabled,
  type TwitchBotSnapshot,
} from './bot-api';
import './workspace.css';

function time(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Unknown'
    : date.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
}

export function ChatBotPage({ role }: { role: 'owner' | 'operator' }) {
  const [bot, setBot] = useState<TwitchBotSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      setBot(await loadBot());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Chat Bot status could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function run(operation: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      const result = await operation();
      if (result && typeof result === 'object' && 'commands' in result) {
        setBot(result as TwitchBotSnapshot);
      }
      setMessage(success);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Chat Bot action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (role !== 'owner') return;
    if (!window.confirm('Disconnect the FFSControl Twitch bot account?\n\nChat commands and automations will stop until it is connected again.')) return;
    await run(disconnectBot, 'Twitch bot disconnected.');
  }

  if (loading && !bot) return <div className="ffs-next-loading">Loading Chat Bot…</div>;

  const ready = Boolean(bot?.configured && bot.connected && bot.scopesReady && bot.broadcasterBotScope);

  return (
    <div className="ffs-workspace-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      {bot ? (
        <>
          <section className="ffs-workspace-hero">
            <div>
              <small>TWITCH CHAT BOT</small>
              <h2>{bot.enabled && ready ? 'Chat Bot is running' : ready ? 'Chat Bot is ready' : 'Chat Bot needs attention'}</h2>
              <p>{bot.botName ? `Connected as ${bot.botName}` : 'Connect the dedicated FFSControl Twitch account to use chat commands and automations.'}</p>
            </div>
            <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={busy}><RefreshCw size={15} /> Refresh</button>
          </section>

          <details className="ffs-workspace-card ffs-bot-connection-detail">
            <summary><strong>Twitch account &amp; connection settings</strong><span>{ready ? 'Connected and authorised' : 'Needs attention'}</span></summary>
            <header><div><small>CONNECTION</small><h3>Twitch accounts</h3></div><strong className={ready ? 'ffs-status good' : 'ffs-status warning'}>{ready ? 'READY' : 'CHECK'}</strong></header>
            <div className="ffs-fact-grid">
              <div><span>Bot account</span><strong>{bot.botName ?? 'Not connected'}</strong></div>
              <div><span>Broadcaster</span><strong>{bot.broadcasterName ?? 'Not connected'}</strong></div>
              <div><span>Bot permissions</span><strong>{bot.scopesReady ? 'Ready' : 'Reconnect required'}</strong></div>
              <div><span>Channel permission</span><strong>{bot.broadcasterBotScope ? 'Ready' : 'Reconnect broadcaster'}</strong></div>
              <div><span>Last Twitch event</span><strong>{time(bot.lastEventAt)}</strong></div>
              <div><span>Last command</span><strong>{time(bot.lastCommandAt)}</strong></div>
            </div>
            {bot.lastError ? <div className="ffs-inline-warning">{bot.lastError}</div> : null}
            {role === 'owner' ? (
              <div className="ffs-inline-actions">
                <button type="button" className="ffs-button primary" disabled={busy || !bot.configured} onClick={() => window.location.assign('/api/v1/integrations/twitch/bot/connect')}>
                  <ExternalLink size={14} /> {bot.connected ? 'Reconnect Bot Account' : 'Connect Bot Account'}
                </button>
                {bot.connected ? <button type="button" className="ffs-button danger" disabled={busy} onClick={() => void disconnect()}><Unplug size={14} /> Disconnect</button> : null}
              </div>
            ) : (
              <div className="ffs-inline-warning">Twitch account authorisation is managed by the owner. All normal Chat Bot controls remain available.</div>
            )}
          </details>

          <section className="ffs-workspace-card">
            <header><div><small>RUNNING STATE</small><h3>Chat Bot</h3></div><Bot size={19} /></header>
            <div className="ffs-primary-action">
              <div>
                <strong>{bot.enabled ? 'Bot enabled' : 'Bot disabled'}</strong>
                <span>{bot.enabled ? 'FFSControl can receive chat events and run enabled commands.' : 'The account stays connected, but commands and chat automations do not run.'}</span>
              </div>
              <button type="button" className={`ffs-button ${bot.enabled ? 'warning' : 'primary'}`} disabled={busy || (!bot.enabled && !ready)} onClick={() => void run(() => setBotEnabled(!bot.enabled), bot.enabled ? 'Chat Bot disabled.' : 'Chat Bot enabled.')}>
                {bot.enabled ? 'Disable Bot' : 'Enable Bot'}
              </button>
            </div>
            <div className="ffs-inline-actions">
              <button type="button" className="ffs-button secondary" disabled={busy || !bot.enabled || !ready} onClick={() => void run(sendBotTestMessage, 'Test message sent to Twitch chat.')}><Send size={14} /> Send Test Message</button>
            </div>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>RECENT ACTIVITY</small><h3>Commands</h3></div><CheckCircle2 size={18} /></header>
            <div className="ffs-activity-list">
              {bot.recent.length === 0 ? <div className="ffs-empty-state">No recent command activity.</div> : bot.recent.map((entry, index) => (
                <div className="ffs-activity-row" key={`${entry.createdAt}-${entry.command}-${index}`}>
                  <time>{time(entry.createdAt)}</time>
                  <strong>!{entry.command}</strong>
                  <span>{entry.userName ?? 'System'}</span>
                  <em>{entry.result}</em>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
